import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'

const CreditCard = forwardRef(({ onError, stripeKeyMetaName = 'stripe-key' , children }, ref) => {
    const cardContainerRef = useRef(null)
    const stripeRef = useRef(null)
    const cardElementRef = useRef(null)
    const [error, setError] = useState({ message: '' })

    useEffect(() => {
        let mounted = true

        function createCard() {
            if (!window.Stripe) {
                console.error('Stripe JS not available on window')
                return
            }

            const key = document.head.querySelector(`meta[name="${stripeKeyMetaName}"]`)
            if (!key) {
                console.error(`Stripe public key not found in meta tag: ${stripeKeyMetaName}.`)
                return
            }

            stripeRef.current = window.Stripe(key.content)

            const elements = stripeRef.current.elements()

            cardElementRef.current = elements.create('card', {
                hidePostalCode: true,
                style: {
                    base: {
                        color: '#254466',
                        fontFamily: '"Open Sans", "Segoe UI", "Roboto", "Helvetica Neue", "Arial", "Noto Sans", sans-serif',
                        fontSmoothing: 'antialiased',
                        fontSize: '16px',
                        fontWeight: '400',
                        '::placeholder': { color: '#718096' }
                    },
                    invalid: {
                        color: '#c53030',
                        iconColor: '#c53030'
                    }
                }
            })

            if (cardContainerRef.current && cardElementRef.current) {
                cardElementRef.current.mount(cardContainerRef.current)

                cardElementRef.current.on('change', (event) => {
                    setError({ message: '' })
                    if (event.error) {
                        setError(event.error)
                        if (onError) onError(event.error)
                    }
                })
            }
        }

        if (!window.Stripe) {
            const script = document.createElement('script')
            script.src = 'https://js.stripe.com/v3/'
            script.async = true
            script.addEventListener('load', () => {
                if (mounted) createCard()
            })
            document.body.appendChild(script)
        } else {
            createCard()
        }

        return () => {
            mounted = false
            try {
                if (cardElementRef.current) {
                    cardElementRef.current.destroy()
                }
            } catch (e) {
                // ignore
            }
        }
    }, [stripeKeyMetaName, onError])

    // Expose submit via ref
    useImperativeHandle(ref, () => ({
        submit(data) {
            if (!stripeRef.current || !cardElementRef.current) {
                return Promise.reject(new Error('Stripe not initialized'))
            }
            return stripeRef.current.createToken(cardElementRef.current, data).then((result) => {
                if (result.error) {
                    // result.error is an object from Stripe
                    const message = result.error.message || 'Unknown Stripe error'
                    // surface through onError and state
                    setError({ message })
                    if (onError) onError(result.error)
                    throw new Error(message)
                }
                return { token: result.token.id }
            })
        }
    }), [])

    return (
        <div>
            <div id="card" ref={cardContainerRef} />
            {/* render children as a function to provide error message similar to Vue slot */}
            {typeof children === 'function' ? children(error.message) : children}
        </div>
    )
})

export default CreditCard
